import React, { useEffect, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { WSPScorecardAtom } from '../../../State/WSPScorecard.Atom'
import { EntriesPerPage } from '../../../Component/Model/Table/Table'
import { DataTable } from '../../../Component/Common/Components/DataTable/DataTable'
import { UserAtom } from '../../../State/user.Atom'
import {  ShowMissingDataMessage, WSPReportPreviewMapper } from './MerSETAReportPreviewTableColumns'
import { MerSETAReport } from '../../../Component/Model/Dto/WSP/WSPReport'
import { MerSETAReportService } from '../../../Component/Api/WSPReport/MerSETAReportService'
import { FilterType, IFilter } from '../../../Component/Common/Components/DataTable/Filters'
import { MerSETALAIService } from '../../../Component/Api/merSETA/LAI/MerSETALAIService'
import { filterColumnValues } from '../../../Component/Model/Dto/ScorecardLicense'
import { defaultObject } from '../../../Helpers/typescriptHelper'
import { ColumnSortOption } from '../../../Component/Model/Dto/ColumnSortOption'
import { Typography, Box, Grid, Button, Alert } from '@mui/material'
import { MDAlert } from '../../../Component/Common/Components/MDAlert'
import { MDProgress } from '../../../Component/Common/Components/MDProgress'
import { MDTypography } from '../../../Component/Common/Components/MDTypography'
import { toast } from 'react-toastify'
import { DataImportType, WSPATRReportType } from '../../../Component/Model/Enum/BEE123Enums'
import { CoreExportService } from '../../../Component/Api/Export/CoreExportService'
import { Export } from '../../../Component/Model/Dto/Export'
import { ExportKeyValue } from '../../../Component/Model/Dto/Upload'
import {useSignalRConnection} from "../../../Hooks/useSignalRConnection";

interface IProps {
  reportType: number;
  isErrorReport: boolean;
}

export const MerSETAReportPreview = ({ reportType, isErrorReport }: IProps) => {
  const WSPScorecard = useRecoilValue(WSPScorecardAtom);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
  const [wspReportData, setWSPReportData] = useState<MerSETAReport[]>([])
  const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
  const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [interventionTitleCodeId, setInterventionTitle] = useState<number | undefined>();
  const [interventionStartDate, setInterventionStartDate] = useState<Date>();
  const [interventionEndDate, setInterventionEndDate] = useState<Date>();
  const [interventionTitles, setInterventionTitles] = useState<filterColumnValues[]>(defaultObject);
  const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption>();
  const [fileExporting, setFileExporting] = useState<boolean>(false);
  const [progress, setProgress] = useState(0);
  const user = useRecoilValue(UserAtom);
  const hub = useSignalRConnection("ImportHub");

  const merSETAReportPreviewFilters: Array<IFilter> = [
    {
      label: 'Intervention Title',
      values: interventionTitles,
      type: FilterType.select, propname: 'interventionTitle',
    },
    {
      label: 'Intervention Date',
      values: [],
      type: FilterType.dateRange, propname: 'interventionDate',
    },
  ]

  const entriesPerPageChanged = (entriesPerPage: number) => {
    setEntriesPerPage({ defaultValue: entriesPerPage });
    setPageNumber(1);
  };

  const pageNumberChanged = (pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const searchValueChanged = (value: string) => {
    setSearchValue(value);
    setPageNumber(1);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      let wspReportPreview = await MerSETAReportService.getWSPReportPreview(WSPScorecard.wspScorecardId, reportType, 1, pageNumber, entriesPerPage.defaultValue!, isErrorReport, searchValue, interventionStartDate, interventionEndDate, interventionTitleCodeId, columnSortOptions);
      setWSPReportData(wspReportPreview.data.results);
      setFilteredItemCount(wspReportPreview.data.filteredItemsCount);
      setTotalItemsCount(wspReportPreview.data.totalItemsCount);
    } catch (error) {
      console.error(error);
      setTotalItemsCount(1);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchStaticData();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if(!hub.isConnected)
        {
          await hub.start();
        }

        hub.on(`exportProgress-${user.username}-${DataImportType[DataImportType.MerSETAReport]}`, (data: any) => {
          setFileExporting(true);
        })
        hub.on(`exportProgressUpdate-${user.username}-${DataImportType[DataImportType.MerSETAReport]}`, (data: any) => {
          setProgress(data.progress);
          setFileExporting(true);
        })
        hub.on(`exportProgressFailed-${user.username}-${DataImportType[DataImportType.MerSETAReport]}`, (data: any) => {
          setFileExporting(false);
          toast.error("Export Failed");
        })
        hub.on(`exportProgressCompleted-${user.username}-${DataImportType[DataImportType.MerSETAReport]}`, (data: any) => {
          setFileExporting(false);
          setProgress(0);
          window.open(data.ExportUrl, "_blank");
        })
      } catch (error) {
        console.error("SignalR connection failed:", error);
      }
    })();
  }, []);

  const fetchStaticData = async () => {
    try {
      const resultTitlesPromise = await MerSETALAIService.GetMerSETAInterventionTitles();
      const interventionTitleData: filterColumnValues[] = resultTitlesPromise.data.map((data) => {
        return { key: data.description, val: data.code };
      });

      setInterventionTitles(interventionTitleData);
    } catch (error) {
      console.error(error);
    }
  }
  const onExportData = async () => {
    setIsLoading(true)
    let params: ExportKeyValue[] = [];
    params.push({ key: "searchValue", value: searchValue });
    params.push({ key: "setaTypeId", value: "1" });
    params.push({ key: "reportType", value: reportType.toString()});
    params.push({ key: "setaScorecardId", value: WSPScorecard.wspScorecardId})
    const data: Export.DynamicFileExportDto = {
      exportFileName: WSPATRReportType[reportType] + " Report",
      importType: DataImportType.MerSETAReport,
      parameters: params
    }
    setFileExporting(true);
    try {
      await CoreExportService.FileExportFunction(data);
    } catch (error) {
      console.log(error);
      toast.error("Export Failed");
      setIsLoading(false);
    } finally {
      params = [];
      setIsLoading(false);
    }
  }


  const filtersChanged = async (filterProps: any) => {
    const { start, end } = filterProps['interventionDate'] || { start: null, end: null };
    const interventionTitle = filterProps['interventionTitle'] || null;

    const startDate = start ? new Date(start) : undefined;
    const endDate = end ? new Date(end) : undefined;

    setInterventionTitle(interventionTitle);
    setInterventionStartDate(startDate);
    setInterventionEndDate(endDate)
    setPageNumber(1);
  };

  const columnSortingChanged = (sortOption: ColumnSortOption) => {
    setColumnSortOptions(sortOption);
    setPageNumber(1);
  };


  useEffect(() => {
    fetchData();
  },
    [WSPScorecard.wspScorecardId,
      entriesPerPage,
      pageNumber,
      searchValue,
      interventionTitleCodeId,
      interventionEndDate,
      interventionStartDate,
      columnSortOptions]
  );

  const workforceProfileDataTable = WSPReportPreviewMapper(wspReportData, isErrorReport);
  const showWarningMessage = ShowMissingDataMessage(wspReportData);
  return (
    <>
      {showWarningMessage && isErrorReport && (<>
     <Box pb={0} sx={{ width: '100%' }}>
        <Alert severity="warning" style={{ paddingTop: 5 }}>
          The highlighted cells represent missing information and this may cause errors when uploading the exported file onto the SETA website. 
          Please ensure that all people and training data is fully captured. 
        </Alert>
      </Box>
      <DataTable
        table={workforceProfileDataTable}
        entriesPerPage={entriesPerPage}
        pageNumber={pageNumber}
        filteredItemsCount={filteredItemCount}
        totalItemsCount={totalItemsCount}
        entriesPerPageChanged={entriesPerPageChanged}
        pageNumberChanged={pageNumberChanged}
        searchValueChanged={searchValueChanged}
        isLoading={isLoading}
        canSearch={true}
        filters={merSETAReportPreviewFilters}
        filtersChanged={filtersChanged}
        manualSorting={true}
        columnSortingChanged={columnSortingChanged}
      />
      </>)}
      {isErrorReport && !showWarningMessage && !isLoading &&
       <Box pb={0} sx={{ width: '100%', height:'100%' }}>
       <Alert variant="filled" color='success' severity="success" style={{ paddingTop: 5 }}>
       We have not detected any missing information in your {WSPATRReportType[reportType]} Report.
       </Alert>
     </Box>
      }
      {!isErrorReport &&
      <>
      <DataTable
        table={workforceProfileDataTable}
        entriesPerPage={entriesPerPage}
        pageNumber={pageNumber}
        filteredItemsCount={filteredItemCount}
        totalItemsCount={totalItemsCount}
        entriesPerPageChanged={entriesPerPageChanged}
        pageNumberChanged={pageNumberChanged}
        searchValueChanged={searchValueChanged}
        isLoading={isLoading}
        canSearch={true}
        filters={merSETAReportPreviewFilters}
        filtersChanged={filtersChanged}
        manualSorting={true}
        columnSortingChanged={columnSortingChanged}
        showSearchBoxInfoIcon = {true}
        searchBoxInfoIconTooltipText='Search by Employee ID, First Name and Last Name.'
      />
      <MDAlert color='light' mt={3}>
        {fileExporting && !isErrorReport ?
          <Grid container justifyContent="center" alignItems="center" direction="column">
            <Typography variant="h6" mb={1}>Exporting file...</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <MDProgress variant="determinate" value={progress} style={{ width: '100%' }} />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(progress)}%`}</Typography>
              </Box>
            </Box>
          </Grid> :
          <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='regular'>
            <p style={{ marginBottom: "0.2rem" }}> Click<Button variant="text" onClick={onExportData} size="small" style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
              to Export data to a file</p>
          </MDTypography>
        }
      </MDAlert>
      </>
    }

      
    </>
  )
}
